import { observable, syncState } from "@legendapp/state";
import type * as Ably from "ably";

import { syncedCrudPersist } from "~/libs/legend-state";
import { getOrgChannel } from "~/libs/pubsub.lib";
import { trpc } from "~/libs/trpc.lib";
import { appDataReady$ } from "../app-data-ready";
import { isSnapshotOrigin } from "../board-snapshot/snapshot-origins";
import type { BoardFilter } from "./board-filters.types";

export const boardFilters$ = observable(
  syncedCrudPersist<BoardFilter>({
    persist: {
      name: "board-filters",
    },
    waitFor: appDataReady$,
    list: async () => {
      return await trpc.boardFilters.list.query();
    },
    update: async ({ id, ...input }) => {
      if (!id) return;
      if (isSnapshotOrigin(id)) return;

      return await trpc.boardFilters.update.mutate({
        id,
        ...input,
      });
    },
    create: async (input) => {
      if (isSnapshotOrigin(input.id)) return input;
      return await trpc.boardFilters.create.mutate(input);
    },
    delete: async (input) => {
      if (!input.id) return;
      if (isSnapshotOrigin(input.id)) return;

      await trpc.boardFilters.delete.mutate({
        id: input.id,
      });
    },
    subscribe: ({ update }) => {
      const channel = getOrgChannel();
      if (!channel) return;

      const handler = (message: Ably.Message) => {
        const items = Array.isArray(message.data)
          ? message.data
          : [message.data];

        // Filter ids with an unsaved local edit still in flight. A broadcast is
        // the server echoing an *earlier* save; if the user has since changed
        // the filter again (e.g. deselected another value), that echo carries a
        // now-stale `value` and would clobber the newer optimistic value,
        // making a just-deselected item pop back selected. Legend's pending
        // overlay guards the local `.set()` path but not this merge, so we drop
        // the echoed `value` for any filter with a pending local change and let
        // the filter's own later echo (once the pending edit saves) reconcile.
        const pendingChanges =
          syncState(boardFilters$).getPendingChanges() ?? {};
        const pendingIds = new Set(
          Object.keys(pendingChanges)
            .map((key) => key.split("/").filter(Boolean)[0])
            .filter(Boolean),
        );

        const filtered = items
          .filter((item) => !isSnapshotOrigin(item?.id))
          .map((item) => {
            if (item?.id && pendingIds.has(item.id) && "value" in item) {
              const { value: _staleValue, ...rest } = item;
              return rest;
            }
            return item;
          });
        if (filtered.length === 0) return;
        update({
          mode: "merge",
          value: filtered,
        });
      };

      channel.subscribe("board-filters", handler);

      return () => {
        channel.unsubscribe("board-filters", handler);
      };
    },
  }),
);

// No `onChange` handler — blocks consume filters reactively through
// `useBlockRift` / `useRiftStat`, so filter mutations propagate via
// the standard observable graph.
